/**
 * Settings ▸ Document templates — the print-template manager (ADR-0021).
 *
 * One FormCard per document kind. Inside each, the available templates
 * (Flint built-in default + the company's own) render as selectable
 * choice-cards — click one to make it the active default ("Use"), the
 * active card carries the sky ring + "In use" pill. Create / duplicate /
 * edit / delete custom ones; the editor previews a draft against sample
 * data via POST /api/pdf-templates/preview. Built-ins are read-only.
 *
 * @see prototype/settings/settings-templates.html — FormCard + choice-card design language
 * @see prototype/settings/settings-numbering.html — edge-to-edge card-body convention
 */
import { useMemo, useState, type ReactNode } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import {
  ArrowLeftRight,
  Banknote,
  Check,
  Coins,
  Copy,
  Eye,
  FileMinus,
  FileText,
  PackageCheck,
  Pencil,
  Plus,
  Receipt,
  ReceiptText,
  ShoppingCart,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import {
  Alert,
  Button,
  ConfirmDialog,
  DialogRoot,
  DialogContent,
  DialogTitle,
  Input,
  StatusPill,
  cn,
  toast,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WEB_ENV } from "../../../../lib/env.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, FormCard } from "../../../../features/settings/index.ts";

export const Route = createFileRoute("/(workspace)/settings/templates/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: TemplatesSettingsPage,
});

type Kind =
  | "invoice"
  | "credit_note"
  | "receipt"
  | "bill"
  | "purchase_order"
  | "goods_receipt"
  | "payment_voucher"
  | "petty_cash_voucher"
  | "transfer_voucher";
interface TemplateRow {
  id: string;
  kind: Kind;
  name: string;
  htmlTemplate: string;
  isDefault: boolean;
  isBuiltIn: boolean;
}

// Every document kind Flint renders a PDF for. Grouped sales → purchases →
// money so the manager reads in the same order as the rest of the app.
const KINDS: ReadonlyArray<{ id: Kind; label: string; desc: string; icon: LucideIcon }> = [
  { id: "invoice", label: "Invoice", desc: "The customer tax invoice.", icon: FileText },
  { id: "credit_note", label: "Credit note", desc: "Reversal of an issued invoice.", icon: FileMinus },
  { id: "receipt", label: "Receipt", desc: "Money received from a customer.", icon: Receipt },
  { id: "bill", label: "Bill", desc: "The vendor / purchase bill.", icon: ReceiptText },
  { id: "purchase_order", label: "Purchase order", desc: "An order issued to a vendor.", icon: ShoppingCart },
  { id: "goods_receipt", label: "Goods receipt", desc: "What arrived against a PO.", icon: PackageCheck },
  { id: "payment_voucher", label: "Payment voucher", desc: "Money paid to a vendor.", icon: Banknote },
  { id: "petty_cash_voucher", label: "Petty cash voucher", desc: "A cash expense from a drawer.", icon: Coins },
  { id: "transfer_voucher", label: "Transfer voucher", desc: "Money moved between accounts.", icon: ArrowLeftRight },
];

interface EditorState {
  mode: "create" | "edit";
  kind: Kind;
  id: string | null; // null in create mode
  name: string;
  html: string;
}

function TemplatesSettingsPage() {
  const utils = trpc.useUtils();
  const listQuery = trpc.pdfTemplates.list.useQuery({});
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TemplateRow | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const invalidate = () => utils.pdfTemplates.list.invalidate();

  const setDefault = trpc.pdfTemplates.setDefault.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Template updated", { description: "This template is now in use." });
    },
    onError: (e: { message: string }) => toast.error("Couldn't update", { description: e.message }),
  });
  const create = trpc.pdfTemplates.create.useMutation();
  const update = trpc.pdfTemplates.update.useMutation();
  const del = trpc.pdfTemplates.delete.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Template deleted");
    },
    onError: (e: { message: string }) => toast.error("Couldn't delete", { description: e.message }),
  });

  const byKind = useMemo(() => {
    const rows = (listQuery.data?.templates ?? []) as TemplateRow[];
    const map = new Map<Kind, TemplateRow[]>();
    for (const k of KINDS) map.set(k.id, []);
    for (const r of rows) map.get(r.kind)?.push(r);
    // Built-in first, then custom alphabetical — stable, scannable order.
    for (const list of map.values()) {
      list.sort((a, b) =>
        a.isBuiltIn === b.isBuiltIn ? a.name.localeCompare(b.name) : a.isBuiltIn ? -1 : 1,
      );
    }
    return map;
  }, [listQuery.data]);

  /** Which template is active for a kind: own default → built-in default. */
  function activeId(rows: TemplateRow[]): string | null {
    return (
      rows.find((r) => r.isDefault && !r.isBuiltIn)?.id ??
      rows.find((r) => r.isDefault && r.isBuiltIn)?.id ??
      null
    );
  }

  async function saveEditor() {
    if (!editor) return;
    const name = editor.name.trim();
    if (!name) {
      toast.error("Name required");
      return;
    }
    try {
      if (editor.mode === "create") {
        await create.mutateAsync({ kind: editor.kind, name, htmlTemplate: editor.html });
      } else if (editor.id) {
        await update.mutateAsync({ id: editor.id, name, htmlTemplate: editor.html });
      }
      await invalidate();
      setEditor(null);
      toast.success("Template saved");
    } catch (e) {
      toast.error("Couldn't save", { description: e instanceof Error ? e.message : String(e) });
    }
  }

  /** Render any (kind, html) pair against sample data and open the PDF. */
  async function runPreview(kind: Kind, html: string) {
    try {
      const res = await fetch(`${WEB_ENV.VITE_FLINT_API_URL}/api/pdf-templates/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, html }),
      });
      if (!res.ok) {
        toast.error("Preview failed", { description: `HTTP ${res.status}` });
        return;
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch (e) {
      toast.error("Preview failed", { description: e instanceof Error ? e.message : String(e) });
    }
  }

  async function preview() {
    if (!editor) return;
    setPreviewing(true);
    try {
      await runPreview(editor.kind, editor.html);
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Document templates" }]}>
      <SettingsShell
        activeSection="templates"
        pageTitle="Document templates"
        pageDescription="Create and edit the PDF templates Flint uses for your printed documents, and choose which one is used for each kind. Built-in Flint templates are read-only — duplicate one to customise it."
      >
        {listQuery.isLoading ? (
          <p className="text-sm text-muted">Loading templates…</p>
        ) : listQuery.error ? (
          <Alert intent="bad" title="Couldn't load templates" description={listQuery.error.message} />
        ) : (
          <div className="flex flex-col gap-5">
            {KINDS.map((k) => {
              const rows = byKind.get(k.id) ?? [];
              const active = activeId(rows);
              return (
                <FormCard
                  key={k.id}
                  icon={<k.icon size={14} strokeWidth={2} aria-hidden />}
                  title={k.label}
                  description={k.desc}
                  badge={
                    <StatusPill tone="muted" size="xs">
                      {rows.length === 1 ? "1 template" : `${rows.length} templates`}
                    </StatusPill>
                  }
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {rows.map((t) => (
                      <TemplateTile
                        key={t.id}
                        template={t}
                        active={t.id === active}
                        onUse={() => setDefault.mutate({ id: t.id })}
                        onPreview={() => void runPreview(t.kind, t.htmlTemplate)}
                        onEdit={() =>
                          setEditor({ mode: "edit", kind: t.kind, id: t.id, name: t.name, html: t.htmlTemplate })
                        }
                        onDuplicate={() =>
                          setEditor({
                            mode: "create",
                            kind: t.kind,
                            id: null,
                            name: `${t.name} (copy)`,
                            html: t.htmlTemplate,
                          })
                        }
                        onDelete={() => setDeleteTarget(t)}
                      />
                    ))}
                    <AddTemplateTile
                      onClick={() =>
                        setEditor({ mode: "create", kind: k.id, id: null, name: "", html: BLANK_TEMPLATE })
                      }
                    />
                  </div>
                </FormCard>
              );
            })}
          </div>
        )}

        {/* Editor */}
        <DialogRoot open={editor !== null} onOpenChange={(o) => !o && setEditor(null)}>
          <DialogContent className="sm:max-w-[880px] p-0 flex flex-col max-h-[92vh]">
            <header className="flex items-center gap-3 border-b border-line px-5 py-4">
              <span
                aria-hidden
                className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-sky-50 text-sky-700"
              >
                <FileText size={15} strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <DialogTitle className="text-sm font-semibold leading-tight text-ink">
                  {editor?.mode === "edit" ? "Edit template" : "New template"}
                </DialogTitle>
                <p className="mt-0.5 text-xs text-muted">
                  {editor ? KINDS.find((k) => k.id === editor.kind)?.label : ""} · HTML + Handlebars.
                  Preview renders against sample data.
                </p>
              </div>
            </header>
            {editor ? (
              <div className="flex flex-1 min-h-0 flex-col gap-3.5 overflow-y-auto px-5 py-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">Template name</span>
                  <Input
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.currentTarget.value })}
                    placeholder="e.g. Invoice — branded"
                    autoFocus
                  />
                </label>
                <label className="flex flex-1 min-h-0 flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">Template HTML</span>
                  <textarea
                    value={editor.html}
                    onChange={(e) => setEditor({ ...editor, html: e.currentTarget.value })}
                    spellCheck={false}
                    className="min-h-[340px] flex-1 rounded-sm border border-line bg-paper p-3 font-mono text-xs leading-relaxed text-ink outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                  />
                  <p className="rounded-sm bg-bg px-3 py-2 text-xs leading-relaxed text-muted">
                    Variables:{" "}
                    <code className="font-mono text-ink-2">{"{{company.legalName}}"}</code>,{" "}
                    <code className="font-mono text-ink-2">{"{{invoice.docNumber}}"}</code>,{" "}
                    <code className="font-mono text-ink-2">{"{{#each lines}}"}</code>. Helpers:{" "}
                    <code className="font-mono text-ink-2">formatPaise</code>,{" "}
                    <code className="font-mono text-ink-2">formatDate</code>,{" "}
                    <code className="font-mono text-ink-2">amountInWords</code>. Duplicate the
                    built-in template for the full field set.
                  </p>
                </label>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2 border-t border-line px-5 py-3">
              <Button
                type="button"
                intent="ghost"
                size="sm"
                className="border border-line"
                disabled={previewing}
                onClick={preview}
              >
                <Eye size={14} /> {previewing ? "Rendering…" : "Preview PDF"}
              </Button>
              <div className="flex items-center gap-2">
                <Button type="button" intent="ghost" size="sm" onClick={() => setEditor(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  intent="primary"
                  size="sm"
                  disabled={create.isPending || update.isPending}
                  onClick={saveEditor}
                >
                  {create.isPending || update.isPending ? "Saving…" : "Save template"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogRoot>

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          intent="danger"
          title="Delete this template?"
          description={
            deleteTarget
              ? `"${deleteTarget.name}" will be removed. If it was in use, the kind falls back to Flint's built-in default.`
              : ""
          }
          confirmLabel="Delete"
          pending={del.isPending}
          onConfirm={async () => {
            if (deleteTarget) await del.mutateAsync({ id: deleteTarget.id });
            setDeleteTarget(null);
          }}
        />
      </SettingsShell>
    </WorkspaceShell>
  );
}

/**
 * One template tile in the per-kind gallery grid. Top row: doc icon +
 * inline icon-actions (Preview always; Edit/Duplicate/Delete depending on
 * whether it's a read-only built-in). Body: name + provenance. Footer: the
 * explicit "use" control — a sky "In use" pill when active, a "Use this
 * template" button otherwise (radio semantics: exactly one active per kind).
 */
function TemplateTile({
  template: t,
  active,
  onUse,
  onPreview,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  template: TemplateRow;
  active: boolean;
  onUse: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={cn(
        "flex min-h-[140px] flex-col rounded-md border p-3.5 transition-colors",
        active
          ? "border-sky-600 bg-sky-50/50 ring-1 ring-sky-600"
          : "border-line bg-paper hover:border-line-2",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          aria-hidden
          className={cn(
            "inline-grid h-8 w-8 place-items-center rounded-md",
            active ? "bg-sky-100 text-sky-700" : "bg-bg text-muted ring-1 ring-line",
          )}
        >
          <FileText size={15} strokeWidth={2} />
        </span>
        <div className="flex items-center gap-0.5">
          <IconAction icon={<Eye size={14} />} label="Preview" onClick={onPreview} />
          {t.isBuiltIn ? (
            <IconAction icon={<Copy size={14} />} label="Duplicate" onClick={onDuplicate} />
          ) : (
            <>
              <IconAction icon={<Pencil size={14} />} label="Edit" onClick={onEdit} />
              <IconAction icon={<Copy size={14} />} label="Duplicate" onClick={onDuplicate} />
              <IconAction icon={<Trash2 size={14} />} label="Delete" tone="danger" onClick={onDelete} />
            </>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex-1">
        <div className="text-sm font-semibold text-ink">{t.name}</div>
        <p className="mt-0.5 text-xs text-muted">
          {t.isBuiltIn ? "Flint built-in · read-only" : "Custom template"}
        </p>
      </div>

      <div className="mt-3">
        {active ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white">
            <Check size={12} strokeWidth={3} /> In use
          </span>
        ) : (
          <Button
            type="button"
            intent="ghost"
            size="sm"
            className="border border-line"
            onClick={onUse}
          >
            Use this template
          </Button>
        )}
      </div>
    </div>
  );
}

/** The trailing dashed cell in each gallery grid — opens a blank editor. */
function AddTemplateTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[140px] flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-line-2 bg-paper text-muted transition-colors hover:border-sky-400 hover:bg-sky-50/40 hover:text-sky-700"
    >
      <Plus size={20} strokeWidth={2} />
      <span className="text-xs font-semibold">New template</span>
    </button>
  );
}

function IconAction({
  icon,
  label,
  tone = "default",
  onClick,
}: {
  icon: ReactNode;
  label: string;
  tone?: "default" | "danger";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "inline-grid h-7 w-7 place-items-center rounded-sm transition-colors",
        tone === "danger"
          ? "text-muted hover:bg-bad-50 hover:text-bad"
          : "text-muted hover:bg-bg hover:text-ink",
      )}
    >
      {icon}
    </button>
  );
}

const BLANK_TEMPLATE = `<!doctype html>
<html>
  <head><meta charset="utf-8" /></head>
  <body style="font-family: Helvetica, Arial, sans-serif; padding: 20mm;">
    <h1>{{company.legalName}}</h1>
    <p>Start from a duplicate of the built-in template for a full layout.</p>
  </body>
</html>
`;
